import { ApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  EmptyStation,
  type FormActionData,
  GenericField,
  InfoPanel,
  MessageBar,
  type ObjectSchemaDefinition,
  Paragraph,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field, useFormikContext } from 'formik';
import {
  ClearUserSignUpWebHookSecretDocument,
  type ClearUserSignUpWebHookSecretMutation,
  type ClearUserSignUpWebHookSecretMutationVariables,
  type IdpUserStore,
  IdpUserStoreDocument,
  type IdpUserStoreQuery,
  type IdpUserStoreQueryVariables,
  type MutationUpdateIdpUserStoreArgs,
  UpdateIdpUserStoreDocument,
  type UpdateIdpUserStoreMutation,
  type UpdateIdpUserStoreMutationVariables,
  useUpdateIdpUserStoreMutation,
} from 'generated/axauth-service';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getManagedServiceAvailabilityMessage } from 'util/managedServiceUtil';
import { validateUrl } from 'util/validationUtil';
import * as Yup from 'yup';
import { ensureError } from '../../../../../common/errors/ensure-error';
import type { SubmitResponse } from './UserSignUpWebHook.types';

const webhookValidation = Yup.object<ObjectSchemaDefinition>({
  userSignUpWebhook: Yup.string()
    .required('User Sign-Up webhook URL is required')
    .test(
      'user-sign-up-webhook-url',
      'User Sign-Up webhook must be a valid URL',
      validateUrl,
    ),
});
type FormData = MutationUpdateIdpUserStoreArgs['input']['patch'];

export const UserSignUpWebHookDetails: React.FC = () => {
  const { userStoreId, envId } = useParams<{
    userStoreId: string;
    envId: string;
  }>();
  const [data, setData] = useState<IdpUserStoreQuery>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const envApolloClient = useRef<ApolloClient<Record<string, unknown>>>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of axauth-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          envApolloClient.current = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
          );

          // Get IdpUserStore data
          const idpUserStoreResult = await envApolloClient.current.query<
            IdpUserStoreQuery,
            IdpUserStoreQueryVariables
          >({
            query: IdpUserStoreDocument,
            variables: {
              id: userStoreId,
            },
            fetchPolicy: 'no-cache',
          });

          setData(idpUserStoreResult.data);
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, userStoreId]);

  const { actions } = useActions(envId, userStoreId);

  const [updateIdpUserStore] = useUpdateIdpUserStoreMutation({
    client: envApolloClient.current,
    fetchPolicy: 'no-cache',
  });

  const saveData = useCallback(
    async (formData: FormData): Promise<SubmitResponse> => {
      envApolloClient.current = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.userServiceAdminBaseUrl).toString(),
      );
      const response = await updateIdpUserStore({
        variables: {
          input: {
            id: userStoreId,
            patch: { userSignUpWebhook: formData.userSignUpWebhook },
          },
        },
      });
      return response.data;
    },
    [envId, updateIdpUserStore, userStoreId],
  );

  const Panel: React.FC = () => {
    const { values } = useFormikContext<IdpUserStore>();
    return (
      <InfoPanel>
        <Section>
          <Paragraph title="Tenant ID">{values?.tenantId}</Paragraph>
          <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const Form: React.FC = () => {
    return (
      <>
        <Field
          name="userSignUpWebhook"
          label="User Sign-Up Webhook URL"
          tooltipContent="A webhook that will receive the signUp-OTP and handles the user's sign-up process. It can be used to customize the user onboarding experience (i.e. send a verification email). After the user is verified, the [completeEndUserSignUp] mutation shall be called to complete the process."
          as={SingleLineTextField}
        />
        <GenericField name="secret" label="User SignUp Webhook Secret">
          {data?.idpUserStore?.userSignUpWebhookSecretIsSet ? (
            <p>*************************************************</p>
          ) : (
            <p>Not Generated</p>
          )}
        </GenericField>
      </>
    );
  };

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="User Sign-Up Webhook">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details<FormData>
          defaultTitle="User Sign-Up Webhook"
          subtitle="User Sign-Up Webhook Details"
          actions={actions}
          validationSchema={webhookValidation}
          initialData={{
            data: data?.idpUserStore,
            loading,
            error,
          }}
          saveData={saveData}
          infoPanel={<Panel />}
        >
          <Form />
        </Details>
      )}
    </>
  );
};

function useActions(
  envId: string,
  userStoreId: string,
): {
  readonly actions: FormActionData<FormData>[];
} {
  const history = useHistory();
  const actions: FormActionData<FormData>[] = [
    {
      label: 'Webhook Secret',
      onActionSelected: () => {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/userSignUp/details/secret/userSignUp`,
        );
      },
    },
    {
      label: 'Delete',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
        );
        await envApolloClient.mutate<
          UpdateIdpUserStoreMutation,
          UpdateIdpUserStoreMutationVariables
        >({
          mutation: UpdateIdpUserStoreDocument,
          variables: {
            input: {
              id: userStoreId,
              patch: { userSignUpWebhook: '' },
            },
          },
        });

        await envApolloClient.mutate<
          ClearUserSignUpWebHookSecretMutation,
          ClearUserSignUpWebHookSecretMutationVariables
        >({
          mutation: ClearUserSignUpWebHookSecretDocument,
          variables: {
            input: { userStoreId: userStoreId },
          },
          fetchPolicy: 'no-cache',
        });
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}`,
        );
      },
    },
  ];

  return { actions } as const;
}

export const UserSignUpWebHookDetailsBreadCrumb: BreadcrumbResolver = () =>
  'User SignUp Webhook Details';
